import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { onInstallAvailable, promptInstall, isStandalone, isIosSafari } from "../pwa";

const DISMISS_KEY = "bragame_pwa_install_dismissed_v1";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function dismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_MS;
  } catch { return false; }
}

/**
 * Floating install banner shown when either
 *   a) Chrome / Edge / Android fired `beforeinstallprompt` (native flow), or
 *   b) The user is on iOS Safari (which never fires the event) — in that
 *      case we show a "Share → Add to Home Screen" tooltip instead.
 *
 * Hidden when the app is already running in standalone mode or the user
 * dismissed within the last 7 days. Not rendered on the /download page
 * (that page has its own dedicated install CTA).
 */
export default function InstallBanner() {
  const [available, setAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [gone, setGone] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) { setGone(true); return; }
    if (dismissed()) { setGone(true); return; }
    // On the dedicated download page we suppress the banner.
    if (window.location.pathname === "/download") { setGone(true); return; }
    if (isIosSafari()) setIos(true);
    return onInstallAvailable((p) => setAvailable(!!p));
  }, []);

  if (gone) return null;
  if (!available && !ios) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setGone(true);
  };

  const doInstall = async () => {
    setInstalling(true);
    const outcome = await promptInstall();
    setInstalling(false);
    if (outcome === "dismissed") dismiss();
    else setGone(true);
  };

  return (
    <div
      className="fixed z-40 left-3 right-3 bottom-3 sm:left-auto sm:right-4 sm:bottom-20 sm:max-w-sm rounded-xl border border-pink/40 bg-elevated/95 backdrop-blur-md shadow-glow-pink"
      role="dialog"
      aria-label="Install Bra Game"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3 p-3">
        <div className="w-10 h-10 rounded-lg shrink-0 overflow-hidden border border-line/60 bg-base flex items-center justify-center">
          <img src="/icons/icon-192.png" alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink font-semibold">Install app</div>
          <div className="text-sm text-ink font-medium leading-tight mt-0.5">
            {ios && !available ? "Add Bra Game to your home screen" : "Install Bra Game — one tap"}
          </div>
          {ios && !available ? (
            <div className="text-[11px] text-ink-2 mt-1 flex items-center gap-1">
              Tap
              <Share className="inline w-3 h-3" />
              then <span className="font-semibold text-ink">Add to Home Screen</span>
            </div>
          ) : (
            <div className="text-[11px] text-ink-3 mt-1">Fast open, standalone window, offline shell</div>
          )}
          {!ios && (
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={doInstall}
                disabled={installing || !available}
                className="h-8 px-3 rounded-md bg-brand-gradient text-white text-xs font-semibold tracking-widest uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pwa-install-btn"
              >
                {installing ? "…" : "Install"}
              </button>
              <button
                onClick={dismiss}
                className="h-8 px-2 rounded-md border border-line text-ink-2 text-[10px] tracking-widest uppercase hover:text-ink"
                data-testid="pwa-install-later"
              >
                Later
              </button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-ink-3 hover:text-ink shrink-0 p-1"
          aria-label="Dismiss install banner"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
